import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import {
  collectVersions,
  type OnFarmImplantQueueRecord,
  OnFarmImplantRequestError,
  useDiscardOnFarmImplantQueueEmbryos,
} from '@/services/on-farm-implant';

import { useSourceTypeLabel } from './columnDefs';

type DiscardQueueEmbryoDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  embryos: OnFarmImplantQueueRecord[];
  onDiscarded: () => void;
};

export const DiscardQueueEmbryoDialog = ({
  open,
  onOpenChange,
  sessionId,
  embryos,
  onDiscarded,
}: DiscardQueueEmbryoDialogProps) => {
  const { t } = useTranslation();
  const sourceTypeLabel = useSourceTypeLabel();

  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: discardQueueEmbryos, isPending } =
    useDiscardOnFarmImplantQueueEmbryos();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) setProblems([]);
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (embryos.length === 0) return;

    setProblems([]);

    try {
      const result = await discardQueueEmbryos({
        sessionId,
        input: {
          sessionQueueIds: embryos.map((embryo) => embryo.sessionQueueId),
          versions: collectVersions(embryos),
        },
      });

      handleOpenChange(false);
      onDiscarded();

      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.implantQueue.discard.success', {
          count: result.totalDiscarded,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error('Failed to discard embryos from the implant queue:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.implantQueue.discard.failed'),
      });
    }
  }, [
    discardQueueEmbryos,
    embryos,
    handleOpenChange,
    onDiscarded,
    sessionId,
    t,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.implantQueue.discard.title')}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.implantQueue.discard.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="py-1 font-medium">
                  {t('onFarmImplant.implantQueue.queueOrder')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.implantQueue.embryo')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.implantQueue.sourceType')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.implantQueue.transferStraw')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.implantQueue.grade')}
                </th>
              </tr>
            </thead>
            <tbody>
              {embryos.map((embryo) => (
                <tr key={embryo.sessionQueueId} className="border-t">
                  <td className="py-1.5">{embryo.queueOrder}</td>
                  <td className="py-1.5">{embryo.embryoName ?? '—'}</td>
                  <td className="py-1.5">
                    {sourceTypeLabel(embryo.sourceType)}
                  </td>
                  <td className="py-1.5">
                    {embryo.transferStrawNumber ?? '—'}
                  </td>
                  <td className="py-1.5">{embryo.grade ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-xs text-gray-500">
            {t('onFarmImplant.implantQueue.discard.permanentWarning')}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={embryos.length === 0 || isPending}
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.implantQueue.discard.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
